import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useLanguage } from '../../i18n/LanguageContext';
import { commonDialogStyles } from '../common/dialogStyles';
import {
  ProgramApiService,
  TrainingProgram,
  ProgramDetail,
  StartingWeight,
} from '../../services/api/ProgramApiService';

interface EnrollmentSetupDialogProps {
  open: boolean;
  program: TrainingProgram | null;
  onClose: () => void;
  onEnrolled: () => void;
}

export const EnrollmentSetupDialog: React.FC<EnrollmentSetupDialogProps> = ({
  open,
  program,
  onClose,
  onEnrolled,
}) => {
  const { t } = useLanguage();
  const [detail, setDetail] = useState<ProgramDetail | null>(null);
  const [weights, setWeights] = useState<Record<number, string>>({});
  const [startingTemplateIndex, setStartingTemplateIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !program) return;

    setLoading(true);
    setError(null);
    setStartingTemplateIndex(0);
    ProgramApiService.getProgramDetails(program.id)
      .then((d) => {
        setDetail(d);
        // Initialise weight fields empty (user can leave blank for defaults)
        const initial: Record<number, string> = {};
        d.templates.forEach((tmpl) =>
          tmpl.exercises.forEach((ex) => {
            initial[ex.program_exercise_id] = '';
          })
        );
        setWeights(initial);
      })
      .catch(() => setError('Failed to load program details'))
      .finally(() => setLoading(false));
  }, [open, program]);

  const handleSubmit = async () => {
    if (!program) return;
    setSubmitting(true);
    setError(null);
    try {
      const startingWeights: StartingWeight[] = Object.entries(weights)
        .filter(([, v]) => v !== '' && !isNaN(parseFloat(v)) && parseFloat(v) > 0)
        .map(([id, v]) => ({ program_exercise_id: Number(id), weight_kg: parseFloat(v) }));

      await ProgramApiService.enroll(program.id, startingWeights, startingTemplateIndex);
      onEnrolled();
      onClose();
    } catch (err: any) {
      if (err.status === 409) {
        setError('You are already enrolled in this program.');
      } else {
        setError(err.message ?? 'Failed to enroll');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Deduplicate exercises across templates
  const uniqueExercises = detail
    ? Array.from(
        new Map(
          detail.templates
            .flatMap((t) => t.exercises)
            .map((ex) => [ex.program_exercise_id, ex] as [number, typeof ex])
        ).values()
      )
    : [];

  const hasMultipleTemplates = (detail?.templates.length ?? 0) > 1;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={commonDialogStyles}>
      <DialogTitle>
        {program ? t('programs.enrollConfirm').replace('{name}', program.name) : ''}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!loading && detail && (
          <>
            {hasMultipleTemplates && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('programs.startingWorkout')}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={startingTemplateIndex}
                  onChange={(_, val) => { if (val !== null) setStartingTemplateIndex(val); }}
                >
                  {detail.templates.map((tmpl) => (
                    <ToggleButton key={tmpl.order_index} value={tmpl.order_index}>
                      {tmpl.name}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('programs.startWeights')} — {t('programs.defaultWeightHint')}
            </Typography>
            {uniqueExercises.map((ex) => (
              <TextField
                key={ex.program_exercise_id}
                label={ex.program_exercise_name}
                placeholder={t('programs.startWeight')}
                type="number"
                size="small"
                fullWidth
                sx={{ mb: 1.5 }}
                value={weights[ex.program_exercise_id] ?? ''}
                onChange={(e) =>
                  setWeights((prev) => ({ ...prev, [ex.program_exercise_id]: e.target.value }))
                }
                slotProps={{ htmlInput: { min: 0, step: 2.5 } }}
              />
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel') || 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || submitting}
        >
          {submitting ? <CircularProgress size={20} /> : t('programs.enroll')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
